import { memo, useMemo } from 'react'

import { FolderItem } from '@inno-sdks/files'
import { ButtonColors, ButtonVariants } from '@inno-ui/button'
import { FileIconSizes, Folder } from '@inno-ui/file-icons'
import {
  ArrowUpRightIcon,
  InformationCircleLineIcon,
  TrashLineIcon
} from '@inno-ui/icons'
import { Modal, ModalProps, ModalVariants } from '@inno-ui/modal'
import { useTranslation } from 'react-i18next'

import { useGetFileItem } from '../../../api'
import { emptyCharacter, maxItemNameLength } from '../../../constants'
import { useOpenItem, usePermissions } from '../../../hooks'
import { FolderPreview } from '../Preview'
import { FolderProperties } from '../Properties'
import { useItemModalOperations } from '../hooks'
import { useItemModal } from '../hooks/useItemModal'

export const FolderModal = memo(() => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop'
  })
  const { itemId, close } = useItemModal()

  const { data: item, isLoading } = useGetFileItem<FolderItem>(itemId)
  const { handleMoveToTrash, handleTitleChange, modalResetKey } =
    useItemModalOperations(item)
  const { canDelete, canWrite } = usePermissions(item?.role)
  const openItem = useOpenItem({ inNewTab: true })

  const modalParams = useMemo<ModalProps>(
    () => ({
      variant: ModalVariants.Full,
      zIndex: 9,
      maxWidth: 1400,
      maxHeight: 831,
      contentFullWidth: true,
      isOpen: !!itemId,
      closeOnClickOutside: false,
      onClose: close,
      rightSideBarWidth: 600,
      sidebarTabId: 1,
      isSidebarOpen: true,
      sidebarTabs: [
        {
          isLoading,
          value: 1,
          label: t('properties'),
          icon: InformationCircleLineIcon,
          body: <FolderProperties />
        }
      ],
      layers: [
        {
          actionsFullWidth: false,
          bodyPaddings: false,
          header: {
            isLoading,
            button: {
              label: t('open-in-new-tab'),
              icon: ArrowUpRightIcon,
              bold: false,
              onClick: () => openItem(item!)
            }
          },
          title: item?.name.replaceAll(emptyCharacter, ''),
          onChangeTitle: !canWrite ? undefined : handleTitleChange,
          titleCharacterLimit: maxItemNameLength,
          isBodyLoading: isLoading,
          icon: item && (() => <Folder size={FileIconSizes.Size48} />),
          body: <FolderPreview />,
          actionsLeft: !canDelete
            ? undefined
            : [
                {
                  label: t('move-to-trash'),
                  icon: TrashLineIcon,
                  color: ButtonColors.Red,
                  variant: ButtonVariants.Ghost,
                  onClick: handleMoveToTrash
                }
              ]
        }
      ]
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemId, item?.name, isLoading, close]
  )

  return <Modal key={`${itemId}-${modalResetKey}`} {...modalParams} />
})
